import { withAuthenticationRequired } from '@auth0/auth0-react'
import { getCurrentEnvironmentName } from '@sparkai/env'
import { identifyUserOnMonitoringSession } from '@sparkai/monitoring'
import { isRunningOnCypress, isRunningOnPlaywright } from '@sparkai/utils'
import { ReactNode, useRef } from 'react'
import { useLocation } from 'react-router-dom-v5-compat'

import { useFetchBearerToken } from '../hooks/useFetchBearerToken'
import { useFetchCurrentUserProfile } from '../hooks/useFetchCurrentUserProfile'
import { useAuthStore } from '../stores/authStore'
import { getReturnToUrl } from '../utils/getReturnToUrl'
import { ErrorMessage } from './ErrorMessage'
import { LoadingMessage } from './LoadingMessage'

export interface ProfileProviderProps {
  children?: ReactNode
}

export function ProfileProvider({ children }: ProfileProviderProps) {
  const location = useLocation()

  const isOnAuthRoute = location.pathname.startsWith('/auth')

  const isMonitoringUserIdentifiedId = useRef<string>()

  const setToken = useAuthStore((state) => state.setToken)
  const setProfile = useAuthStore((state) => state.setUserProfile)

  const { isErrorFetchingBearerToken } = useFetchBearerToken(setToken)

  const { isErrorFetchingProfile } = useFetchCurrentUserProfile((profile) => {
    setProfile(profile)

    if (isMonitoringUserIdentifiedId.current !== profile.app_user_id) {
      isMonitoringUserIdentifiedId.current = profile.app_user_id
      identifyUserOnMonitoringSession({
        id: profile.app_user_id,
        email: profile.email,
        role: profile.role,
      })
    }
  })
  const isReady = useAuthStore((state) => state.token && state.userProfile)
  const isError = isErrorFetchingBearerToken || isErrorFetchingProfile

  if (isOnAuthRoute || isRunningOnCypress || isRunningOnPlaywright) return <>{children}</>

  if (isError) {
    return <ErrorMessage errorType={getErrorType(isErrorFetchingBearerToken, isErrorFetchingProfile)} />
  }

  if (isReady) {
    return <RequiredAuth>{children}</RequiredAuth>
  }

  return (
    <RequiredAuth>
      <LoadingMessage />
    </RequiredAuth>
  )
}

const getErrorType = (isErrorFetchingBearerToken: boolean, isErrorFetchingProfile: boolean) => {
  if (isErrorFetchingBearerToken) {
    return 'failed-token-load'
  }
  if (isErrorFetchingProfile) {
    return 'failed-profile-load'
  }

  return 'unknown'
}
function NoopComponent({ children }: { children: ReactNode }) {
  return <>{children}</>
}

const isOnAnSandboxEnvironment = getCurrentEnvironmentName().includes('sandbox')
const RequiredAuth =
  isRunningOnCypress || isRunningOnPlaywright
    ? NoopComponent
    : withAuthenticationRequired(
        function RequiredAuth({ children }: { children: ReactNode }) {
          return <>{children}</>
        },
        {
          loginOptions: {
            'ext-is-sandbox': isOnAnSandboxEnvironment ? 'true' : 'false',
          },
          returnTo: getReturnToUrl,
        }
      )
